import {productLib} from '@nuskin/product-lib';
import {RunConfigService, UrlService, BrowserDetection} from '@nuskin/ns-util';
import { getConfiguration } from '@nuskin/configuration-sdk';
import {AccountManager} from '@nuskin/ns-account';
import {PriceType, Product} from '@nuskin/ns-product-lib';

const extractProductData = (data, sku) => {
    let retVal;

    if (Array.isArray(data.variants) && data.variants.length > 0) {
        retVal = data.variants.find((variant) => variant.sku === sku) || data.variants[0];
        retVal.variantSelectLabel = data.variantSelectLabel;
    } else if (data.bundle) {
        retVal = data.bundle;
        retVal.title = data.title;
        retVal.productImages = data.productImages;
    }

    return retVal;
}

const addPrice = (productData, priceTypeKey, subKey, priceValue) => {
    if (!['Points', 'cv', 'price', 'pv'].includes(priceTypeKey)) {
        // if the priceTypeKey is not one of these, then we don't want it to show.
        return;
    }
    if (priceTypeKey === 'pv') {
        priceTypeKey = 'psv';
    }
    if (priceTypeKey === 'cv') {
        priceTypeKey = 'csv';
    }

    if (productData[priceTypeKey] === undefined) {
        if (priceTypeKey === 'Points') {
            if (!productData.price) {
                productData.price = {};
            }
            productData.price.PTS = priceValue;
            return;
        } else {
            productData[priceTypeKey] = {};
        }
    }
    productData[priceTypeKey][subKey] = priceValue;
}

const convertPricing = (pricing, productData) => {
    if (pricing) {
        Object.keys(pricing).forEach((channelKey) => {
            Object.keys(pricing[channelKey]).forEach((contextKey) => {
                Object.keys(pricing[channelKey][contextKey] || {}).forEach((priceTypeKey) => {
                    const priceValue = pricing[channelKey][contextKey][priceTypeKey];
                    switch (channelKey) {
                        case 'wholesale':
                            switch (contextKey) {
                                case 'webOrder':
                                    addPrice(productData, priceTypeKey, 'WWHL', priceValue);
                                    break;
                                case 'webAdr':
                                case 'WADW':
                                case 'ADR5':
                                    addPrice(productData, priceTypeKey, 'WADW', priceValue);
                                    break;
                                case 'order':
                                case 'WHL':
                                    addPrice(productData, priceTypeKey, 'WHL', priceValue);
                                    break;
                                case 'adr':
                                    addPrice(productData, priceTypeKey, 'ADW', priceValue);
                                    break;
                                case 'ADR10':
                                case 'WADWD':
                                    addPrice(productData, priceTypeKey, 'WADWD', priceValue);
                                    break;
                                default:
                                    addPrice(
                                        productData,
                                        priceTypeKey,
                                        `${contextKey}-WWHL`,
                                        priceValue
                                    );
                                    break;
                            }
                            break;
                        case 'retail':
                            switch (contextKey) {
                                case 'webOrder':
                                    addPrice(productData, priceTypeKey, 'WRTL', priceValue);
                                    break;
                                case 'webAdr':
                                    addPrice(productData, priceTypeKey, 'WADR', priceValue);
                                    break;
                                case 'order':
                                case 'RTL':
                                    addPrice(productData, priceTypeKey, 'RTL', priceValue);
                                    break;
                                case 'adr':
                                    addPrice(productData, priceTypeKey, 'ADR', priceValue);
                                    break;
                                default:
                                    addPrice(
                                        productData,
                                        priceTypeKey,
                                        `${contextKey}-WRTL`,
                                        priceValue
                                    );
                                    break;
                            }
                            break;
                    }
                });
            });
        });
    }
}

const createDataFromSkuSearch = (searchData, options) => {
    if (!searchData) {
        return new Product();
    }
    let product = new Product();

    if (searchData) {
        product.sku = options.sku;
        product.country = options.country;
        product.lang = options.language;
        product.isExclusive = searchData.isExclusive;
        if (Array.isArray(searchData.productImages) && searchData.productImages.length > 0) {
            product.setFullImage(searchData.productImages[0].url);
            product.setThumbnail(searchData.productImages[0].thumbnail);
            product.setImageAltText(searchData.productImages[0].altText);
            // product.setProductCarouselImages(???searchData.contents.imageCarousel);
        }
        // product.productLabels = ???
        product.title = searchData.title;
        product.shortDescr = searchData.description;
        // product.longDescr = langObj['longDescription']; looks like it is deprecated
        product.dangerousGoods = searchData.dangerousGoods === true;
        product.ingredients = searchData.ingredients;
        product.benefits = searchData.benefits;
        product.usage = searchData.usage;
        product.resources = searchData.resources;
        // product.videos = searchData.???;
        // product.contentSection =searchData.???;
        // product.sizeWeight = ???;
        product.size = searchData.size;
        product.nettoWeight = searchData.nettoWeight;
        product.salesLabel = searchData.salesLabel;
        // product.movie = searchData.???;
        // product.youtube = searchData.???;
        product.salesEventText = searchData.salesText;
        product.scanQualified = searchData.scanQualifiedCount;
        // product.division = searchData.???
        product.custTypes = Array.isArray(searchData.customerTypes) && searchData.customerTypes.map((ct) => {
            if (ct === 'Preferred') return '30';
            else if (ct === 'Retail') return '20';
            else if (ct === '') return '10';
        });
        product.restrictedMarkets = searchData.restrictedMarkets;
        // if (addOns) {
        //     product.addOns = addOns;
        // } else {
        //     product.addOns = [];
        // }
        if (options.isGroupOffer) product.isGroupOffer = options.isGroupOffer;
        if (options.isPersonalOffer) product.isPersonalOffer = options.isPersonalOffer;

        product.variantsLabel = searchData.variantLabel;
        product.variantDropdownLabel = searchData.variantSelectLabel;
        product.shade = searchData.variantColor;

        const pricing = JSON.parse(searchData.pricingJson);
        const orderTypes = [];
        if (searchData.purchaseTypes.buyOnce) orderTypes.push('order');
        if (searchData.purchaseTypes.subscription) orderTypes.push('adr');
        let status = {
            sku: options.sku,
            globalProductId: searchData.globalId,
            status: searchData.status.status === 'sellable' ? 'RELEASED_FOR_SALE' : 'NOT_RELEASED_FOR_SALE',
            availableQuantity: searchData.availableQuantity,
            maxQuantity: searchData.maxQuantity,
            locallyProduced: searchData.locallyProduced,  // ???
            backorderedAvailableDate: searchData.status.isBackordered > 0 ? searchData.status.backorderedAvailableDate : null,
            orderType: orderTypes,  // searchData.channels.map((channel) => channel.toLowerCase()),
            childSkus: null // ???
        };
        convertPricing(pricing, status);
        product.addPricingFromStatus(status);
    }

    // Note: this mapping was originally created for the product data in AEM. This product
    // data comes from content stack and is not identical to what is in AEM. There may
    // need to be some tweaks in here. ESPECIALLY when the data gets moved to the
    // bundle field (vs variants) in the product data.

    return product;
}

const handleDetailResponse = async (responseData, options) => {
    let retVal = {success: true},
        dataForProduct = createDataFromSkuSearch(responseData, options);

    if (dataForProduct.sku) {
        retVal.product = new Product(dataForProduct);

        const siteUrl = UrlService.getSiteUrl();
        const isEdge = BrowserDetection.isEdge();
        const isFirefox = BrowserDetection.isFirefox();

        // set the base url for images, etc
        retVal.product.setBaseUrl(siteUrl);

        // fix image paths for non-edge/firefox
        if (!isEdge && !isFirefox) {
            if (retVal.product.fullImage && !retVal.product.fullImage.includes("media.nuskin.com/transform")) {
                retVal.product.fullImage = retVal.product.fullImage
                    ? `${retVal.product.fullImage.split("?")[0]}?format=pjpg`
                    : undefined;
                retVal.product.thumbnail = retVal.product.thumbnail
                    ? `${retVal.product.thumbnail.split("?")[0]}?format=pjpg`
                    : undefined;
            }
        }
    } else {
        retVal.success = false;
        retVal.failedSku = options.sku;
    }

    if (retVal.product && options.priceType) {
        const {Cart: cartCfg} = await getConfiguration(['Cart']);
        const priceType = cartCfg.showWholeSalePricing && !AccountManager.isLoggedIn()
            ? PriceType.WWHL
            : options.priceType;
        retVal.product.setPriceAndPvFromType(priceType);
    }

    return retVal;
}

const getProductDetail = async (options) => {
    let response;

    // Hit the PMD and get all the data for the product including the price information.
    try {
        response = await productLib.getProduct({
            fromId: options.sku,
            env: RunConfigService.getEnvironmentCode(),
            market: options.country,
            language: options.language
        })
    } catch (e) {
        console.log(e);
        response.data = {};
    }

    return extractProductData(response.data, options.sku);
}

const getProductBySku = async (options) => {
    const data = await getProductDetail(options);

    let result = await handleDetailResponse(data, options);

    return result;
}

const getProductData = async (options) => {
    const data = await getProductDetail(options);

    return createDataFromSkuSearch(data, options);
}

export {
    getProductBySku,
    getProductData
}